import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { colmenasAPI, zonasAPI, fotoColmenaAPI } from '../api/client';
import { useTranslation } from 'react-i18next';

const TIPOS = ['Langstroth', 'Dadant', 'Dadant modificada', 'Colmena rustica', 'Top-bar', 'Flow Hive', 'Otro'];

// Semáforo: mismo vocabulario visual que la ficha de colmena.
const SEMAFORO = {
  verde:    { bg: '#E8F5EC', color: '#4A7C59', icon: '🟢', labelKey: 'estado.semOk' },
  amarillo: { bg: '#FDF0DC', color: '#C97820', icon: '🟡', labelKey: 'estado.semAviso' },
  rojo:     { bg: '#FDECEA', color: '#C0392B', icon: '🔴', labelKey: 'estado.semAlerta' },
};
// Orden por severidad: rojas arriba, amarillas después, verdes al final.
const SEM_ORDEN = { rojo: 0, amarillo: 1, verde: 2 };

export default function Colmenas() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [colmenas, setColmenas] = useState([]);
  const [zonas, setZonas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [filtroZona, setFiltroZona] = useState('');
  const [filtroActiva, setFiltroActiva] = useState('activas');
  const [importModal, setImportModal] = useState(false);
  const [openMenuId, setOpenMenuId] = useState(null);

  useEffect(() => {
    if (!openMenuId) return;
    const close = () => setOpenMenuId(null);
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, [openMenuId]);

  const load = () => Promise.all([colmenasAPI.list(), zonasAPI.list()])
    .then(([c, z]) => { setColmenas(c.data); setZonas(z.data); }).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  // Los filtros se aplican primero y el orden por severidad después, para que
  // ordenar y filtrar sigan siendo independientes entre sí.
  const filtered = colmenas
    .filter(c => {
      if (filtroZona && c.zona_id !== parseInt(filtroZona)) return false;
      if (filtroActiva === 'activas' && !c.activa) return false;
      if (filtroActiva === 'inactivas' && c.activa) return false;
      return true;
    })
    .sort((a, b) => {
      const sa = SEM_ORDEN[a.semaforo] ?? SEM_ORDEN.verde;
      const sb = SEM_ORDEN[b.semaforo] ?? SEM_ORDEN.verde;
      if (sa !== sb) return sa - sb;
      return (a.nombre || '').localeCompare(b.nombre || '');
    });

  const abrirFicha = (c) => navigate(`/colmenas/${c.id}`);

  return (
    <div>
      <div style={S.header}>
        <div>
          <h1 style={S.title}>{t('colmenas.title')}</h1>
          <p style={S.sub}>{t('colmenas.countSub', { count: filtered.length, s: filtered.length !== 1 ? 's' : '' })}</p>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          <button className="btn btn-ghost"
            onClick={() => zonas.length === 0 ? alert(t('colmenas.noZoneAlert')) : setImportModal(true)}>
            📥 {t('colmenas.importBtn')}
          </button>
          <button className="btn btn-primary"
            onClick={() => zonas.length === 0 ? alert(t('colmenas.noZoneAlert')) : setModal({ mode: 'create' })}>
            {t('colmenas.newBtn')}
          </button>
        </div>
      </div>

      <div style={S.filters}>
        <select className="form-input" style={{ width: 'auto', minWidth: 140 }} value={filtroZona} onChange={e => setFiltroZona(e.target.value)}>
          <option value="">{t('common.allZones')}</option>
          {zonas.map(z => <option key={z.id} value={z.id}>{z.nombre}</option>)}
        </select>
        <div style={S.tabs}>
          {[['activas', t('colmenas.activeFilter')], ['inactivas', t('colmenas.inactiveFilter')], ['todas', t('colmenas.allFilter')]].map(([f, label]) => (
            <button key={f} onClick={() => setFiltroActiva(f)}
              style={{ ...S.tab, ...(filtroActiva === f ? S.tabActive : {}) }}>
              {label}
            </button>
          ))}
        </div>
      </div>

      {loading ? <div style={{ textAlign: 'center', padding: 60, color: '#8B6355' }}>{t('common.loading')}</div>
        : zonas.length === 0 ? (
          <div className="empty-state">
            <div className="icon">📍</div>
            <h3>{t('colmenas.noZoneEmpty')}</h3>
            <p>{t('colmenas.noZoneEmptyMsg')}</p>
            <a href="/zonas" className="btn btn-primary">{t('colmenas.noZoneBtn')}</a>
          </div>
        ) : filtered.length === 0 ? (
          <div className="empty-state">
            <div className="icon">🐝</div>
            <h3>{t('colmenas.emptyTitle')}</h3>
            <button className="btn btn-primary" onClick={() => setModal({ mode: 'create' })}>{t('colmenas.newBtn')}</button>
          </div>
        ) : (
          <div style={S.grid}>
            {filtered.map(c => {
              const sem = SEMAFORO[c.semaforo] || SEMAFORO.verde;
              const razones = c.alertas_activas || [];
              return (
                <div
                  key={c.id}
                  className="card card-body"
                  style={{ overflow: 'hidden', padding: 0, cursor: 'pointer', borderLeft: `4px solid ${sem.color}` }}
                  role="link"
                  tabIndex={0}
                  title={t('ficha.openBtn')}
                  onClick={() => abrirFicha(c)}
                  onKeyDown={e => {
                    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); abrirFicha(c); }
                  }}
                >
                  {c.tiene_foto ? (
                    <img
                      src={fotoColmenaAPI.imagenUrl(c.id)}
                      alt={c.nombre}
                      style={{ display: 'block', width: '100%', height: 140, objectFit: 'cover' }}
                    />
                  ) : null}
                  <div style={{ padding: '16px 20px' }}>
                    <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', marginBottom: 10 }}>
                      {!c.tiene_foto && <span style={{ fontSize: 22 }}>🐝</span>}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={S.cardName}>{c.nombre}</div>
                        {c.numero && <div style={{ fontSize: 12, color: '#8B6355' }}>#{c.numero}</div>}
                      </div>
                      <span className={`badge ${c.activa ? 'badge-green' : 'badge-gray'}`}>{c.activa ? t('common.active') : t('common.inactive')}</span>
                      <div style={{ position: 'relative' }}>
                        <button
                          type="button"
                          title={t('colmenas.manageMenu')}
                          aria-label={t('colmenas.manageMenu')}
                          onClick={e => { e.stopPropagation(); setOpenMenuId(openMenuId === c.id ? null : c.id); }}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 18, color: '#8B6355', padding: '2px 6px', borderRadius: 6, lineHeight: 1 }}
                        >⋯</button>
                        {openMenuId === c.id && (
                          <div
                            onClick={e => e.stopPropagation()}
                            style={{ position: 'absolute', right: 0, top: '100%', background: 'white', borderRadius: 10, boxShadow: '0 4px 16px rgba(44,26,14,0.15)', border: '1px solid #FAE0B0', zIndex: 10, minWidth: 130, padding: 4 }}
                          >
                            <button
                              onClick={e => { e.stopPropagation(); setOpenMenuId(null); setModal({ mode: 'edit', colmena: c }); }}
                              style={{ display: 'block', width: '100%', padding: '8px 14px', border: 'none', background: 'none', cursor: 'pointer', fontSize: 13, color: '#2C1A0E', borderRadius: 6, textAlign: 'left', fontFamily: "'DM Sans', sans-serif" }}
                            >✏️ {t('common.edit')}</button>
                            <button
                              onClick={e => { e.stopPropagation(); setOpenMenuId(null); setDeleting(c); }}
                              style={{ display: 'block', width: '100%', padding: '8px 14px', border: 'none', background: 'none', cursor: 'pointer', fontSize: 13, color: '#C0392B', borderRadius: 6, textAlign: 'left', fontFamily: "'DM Sans', sans-serif" }}
                            >🗑️ {t('common.delete')}</button>
                          </div>
                        )}
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 8 }}>
                      <span style={{ ...S.semaforoBadge, background: sem.bg, color: sem.color }}>
                        {sem.icon} {t(sem.labelKey)}
                      </span>
                      <span className="badge badge-amber">📍 {c.zona_nombre}</span>
                    </div>
                    {razones.length > 0 && (
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center', marginBottom: 8 }}>
                        <span style={{ fontSize: 11, color: '#8B6355', fontWeight: 600 }}>{t('ficha.reasonsLabel')}:</span>
                        {razones.map(k => (
                          <span key={k} style={S.razonChip}>{t('estados.' + k, { defaultValue: k })}</span>
                        ))}
                      </div>
                    )}
                    {c.notas && <p style={{ fontSize: 12, color: '#8B6355', fontStyle: 'italic', padding: '8px', background: '#F8F4EF', borderRadius: 6, marginBottom: 8 }}>{c.notas}</p>}
                  </div>
                </div>
              );
            })}
          </div>
        )}

      {modal && (
        <ColmenaModal mode={modal.mode} colmena={modal.colmena} zonas={zonas}
          onSave={async (data, fotoFile, deleteFoto) => {
            let res;
            if (modal.mode === 'create') {
              res = await colmenasAPI.create(data);
            } else {
              res = await colmenasAPI.update(modal.colmena.id, data);
            }
            const colmenaId = res.data.id;
            if (deleteFoto) await fotoColmenaAPI.delete(colmenaId);
            if (fotoFile) await fotoColmenaAPI.upload(colmenaId, fotoFile);
            setModal(null);
            load();
          }}
          onClose={() => setModal(null)} />
      )}

      {deleting && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setDeleting(null)}>
          <div className="modal">
            <div className="modal-header"><h2>{t('colmenas.deleteTitle')}</h2></div>
            <div className="modal-body">
              <p>{t('colmenas.deleteConfirm', { name: deleting.nombre })}</p>
              <p style={{ color: '#C0392B', fontSize: 13, marginTop: 8 }}>{t('colmenas.deleteWarning')}</p>
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={() => setDeleting(null)}>{t('common.cancel')}</button>
              <button className="btn btn-danger" onClick={async () => { await colmenasAPI.delete(deleting.id); setDeleting(null); load(); }}>{t('common.delete')}</button>
            </div>
          </div>
        </div>
      )}

      {importModal && zonas.length > 0 && (
        <ImportModal
          zonas={zonas}
          onClose={() => setImportModal(false)}
          onDone={() => { load(); }}
        />
      )}
    </div>
  );
}

function ColmenaModal({ mode, colmena, zonas, onSave, onClose }) {
  const { t } = useTranslation();
  const [form, setForm] = useState({
    nombre: colmena?.nombre || '', numero: colmena?.numero || '',
    tipo: colmena?.tipo || 'Langstroth', notas: colmena?.notas || '',
    activa: colmena?.activa ?? true, zona_id: colmena?.zona_id || zonas[0]?.id || '',
    fecha_instalacion: colmena?.fecha_instalacion || '',
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const [fotoFile, setFotoFile] = useState(null);
  const [fotoPreview, setFotoPreview] = useState(null);
  const [deleteFoto, setDeleteFoto] = useState(false);
  const fotoInputRef = useRef(null);
  const set = (k, v) => setForm(f => ({...f, [k]: v}));

  const tieneActual = mode === 'edit' && colmena?.tiene_foto && !deleteFoto;

  const onFileChange = (e) => {
    const f = e.target.files[0];
    if (!f) return;
    setFotoFile(f);
    setDeleteFoto(false);
    setFotoPreview(URL.createObjectURL(f));
  };

  const onRemoveFoto = () => {
    setFotoFile(null);
    setFotoPreview(null);
    setDeleteFoto(tieneActual);
    if (fotoInputRef.current) fotoInputRef.current.value = '';
  };

  const submit = async e => {
    e.preventDefault(); setErr(''); setSaving(true);
    try {
      await onSave({
        ...form,
        zona_id: parseInt(form.zona_id),
        // Opcional: si el usuario no la rellena, la colmena se queda sin fecha.
        fecha_instalacion: form.fecha_instalacion || null,
      }, fotoFile, deleteFoto);
    }
    catch (ex) { setErr(ex.response?.data?.detail || t('common.error')); }
    finally { setSaving(false); }
  };

  const previewSrc = fotoPreview
    ? fotoPreview
    : tieneActual
      ? fotoColmenaAPI.imagenUrl(colmena.id)
      : null;

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal">
        <div className="modal-header">
          <h2>{mode === 'create' ? t('colmenas.modalCreate') : t('colmenas.modalEdit')}</h2>
          <button className="btn btn-ghost btn-icon" onClick={onClose}>✕</button>
        </div>
        <form onSubmit={submit}>
          <div className="modal-body">
            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 12, overflow: 'hidden' }}>
              <div className="form-group" style={{ minWidth: 0 }}>
                <label className="form-label">{t('colmenas.nameLabel')}</label>
                <input value={form.nombre} onChange={e => set('nombre', e.target.value)} className="form-input" placeholder={t('colmenas.namePlaceholder')} required />
              </div>
              <div className="form-group" style={{ minWidth: 0 }}>
                <label className="form-label">{t('colmenas.numberLabel')}</label>
                <input value={form.numero} onChange={e => set('numero', e.target.value)} className="form-input" placeholder={t('colmenas.numberPlaceholder')} />
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">{t('colmenas.zoneLabel')}</label>
              <select value={form.zona_id} onChange={e => set('zona_id', e.target.value)} className="form-input" required>
                {zonas.map(z => <option key={z.id} value={z.id}>{z.nombre}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">{t('colmenas.typeLabel')}</label>
              <select value={form.tipo} onChange={e => set('tipo', e.target.value)} className="form-input">
                {TIPOS.map(tp => <option key={tp} value={tp}>{t('colmenas.tipos.' + tp, { defaultValue: tp })}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">
                {t('colmenas.installDateLabel')} <span style={{ color: '#8B6355', fontWeight: 400 }}>{t('colmenas.photoOptional')}</span>
              </label>
              <input type="date" value={form.fecha_instalacion}
                onChange={e => set('fecha_instalacion', e.target.value)} className="form-input" />
              <p style={{ fontSize: 11, color: '#B09080', marginTop: 6 }}>{t('colmenas.installDateHint')}</p>
            </div>
            <div className="form-group">
              <label className="form-label">{t('colmenas.notesLabel')}</label>
              <textarea value={form.notas} onChange={e => set('notas', e.target.value)} className="form-input" placeholder={t('colmenas.notesPlaceholder')} />
            </div>

            <div className="form-group">
              <label className="form-label">{t('colmenas.photoLabel')} <span style={{ color: '#8B6355', fontWeight: 400 }}>{t('colmenas.photoOptional')}</span></label>
              {previewSrc ? (
                <div style={{ position: 'relative', marginBottom: 8 }}>
                  <img src={previewSrc} alt={t('colmenas.photoPreview')}
                    style={{ width: '100%', height: 160, objectFit: 'cover', borderRadius: 10, display: 'block', border: '1px solid #FAE0B0' }} />
                  <button type="button" onClick={onRemoveFoto}
                    style={{ position: 'absolute', top: 8, right: 8, background: 'rgba(0,0,0,0.55)', color: 'white', border: 'none', borderRadius: 20, padding: '3px 10px', fontSize: 12, cursor: 'pointer' }}>
                    {t('colmenas.photoRemove')}
                  </button>
                </div>
              ) : (
                <button type="button" className="btn btn-ghost btn-sm"
                  onClick={() => fotoInputRef.current && fotoInputRef.current.click()}
                  style={{ width: '100%', border: '1.5px dashed #FAE0B0', borderRadius: 10, padding: 14, color: '#8B6355' }}>
                  {t('colmenas.photoAdd')}
                </button>
              )}
              <input ref={fotoInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={onFileChange} />
              {!previewSrc && (
                <p style={{ fontSize: 11, color: '#B09080', marginTop: 6 }}>{t('colmenas.photoHint')}</p>
              )}
            </div>

            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 14 }}>
              <input type="checkbox" checked={form.activa} onChange={e => set('activa', e.target.checked)} />
              {t('colmenas.activeCheck')}
            </label>
            {err && <div style={{ background: '#FDECEA', color: '#C0392B', padding: 10, borderRadius: 8, marginTop: 12, fontSize: 13 }}>{err}</div>}
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-ghost" onClick={onClose}>{t('common.cancel')}</button>
            <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? t('common.saving') : mode === 'create' ? t('colmenas.createBtn') : t('colmenas.saveBtn')}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function ImportModal({ zonas, onClose, onDone }) {
  const { t } = useTranslation();
  const [zonaId, setZonaId] = useState(zonas[0]?.id || '');
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState('');
  const fileRef = useRef(null);

  const downloadTemplate = (sep) => {
    const header = ['nombre', 'numero', 'tipo', 'notas'].join(sep);
    const row = ['Colmena Ejemplo', '001', 'Langstroth', 'Colonia fuerte zona norte'].join(sep);
    const csv = header + '\n' + row + '\n';
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = sep === ',' ? 'plantilla_colmenas.csv' : 'plantilla_colmenas_sc.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const submit = async () => {
    if (!file || !zonaId) return;
    setLoading(true); setErr(''); setResult(null);
    try {
      const res = await colmenasAPI.importCSV(zonaId, file);
      setResult(res.data);
      onDone();
    } catch (ex) {
      setErr(ex.response?.data?.detail || t('common.error'));
    } finally { setLoading(false); }
  };

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal">
        <div className="modal-header">
          <h2>{t('colmenas.importTitle')}</h2>
          <button className="btn btn-ghost btn-icon" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          {!result ? (
            <>
              <div className="form-group">
                <label className="form-label">{t('colmenas.importZoneLabel')}</label>
                <select value={zonaId} onChange={e => setZonaId(e.target.value)} className="form-input">
                  {zonas.map(z => <option key={z.id} value={z.id}>{z.nombre}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">{t('colmenas.importFileLabel')}</label>
                <input ref={fileRef} type="file" accept=".csv" className="form-input"
                  style={{ padding: '6px 10px' }}
                  onChange={e => setFile(e.target.files[0] || null)} />
              </div>
              {err && <div style={{ background: '#FDECEA', color: '#C0392B', padding: 10, borderRadius: 8, fontSize: 13 }}>{err}</div>}
              <div style={{ marginTop: 16, padding: '14px 16px', background: '#F8F4EF', borderRadius: 10, border: '1px solid #FAE0B0' }}>
                <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 10, color: '#5C3D2E' }}>{t('colmenas.templateTitle')}</div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
                  <button type="button" className="btn btn-ghost btn-sm" onClick={() => downloadTemplate(',')}>
                    📥 {t('colmenas.templateComma')}
                  </button>
                  <button type="button" className="btn btn-ghost btn-sm" onClick={() => downloadTemplate(';')}>
                    📥 {t('colmenas.templateSemicolon')}
                  </button>
                </div>
                <p style={{ fontSize: 12, color: '#8B6355', margin: 0 }}>{t('colmenas.templateHint')}</p>
              </div>
            </>
          ) : (
            <div>
              <div style={{ background: '#E8F5EC', borderRadius: 10, padding: '12px 16px', marginBottom: 12 }}>
                <div style={{ fontWeight: 500, marginBottom: 6, color: '#1A5C30' }}>{t('colmenas.importResult')}</div>
                <div style={{ fontSize: 14, color: '#2C1A0E' }}>
                  ✅ {t('colmenas.importedCount', { count: result.importadas, s: result.importadas !== 1 ? 's' : '' })}
                  {' '}&middot;{' '}
                  ⏭️ {t('colmenas.omittedCount', { count: result.omitidas, s: result.omitidas !== 1 ? 's' : '' })}
                </div>
              </div>
              {result.errores?.length > 0 && (
                <div style={{ background: '#FDECEA', borderRadius: 10, padding: '12px 16px' }}>
                  <div style={{ fontWeight: 500, marginBottom: 6, color: '#C0392B' }}>{t('colmenas.importErrors')}</div>
                  {result.errores.map((e, i) => (
                    <div key={i} style={{ fontSize: 13, color: '#C0392B', marginBottom: 2 }}>{e}</div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost" onClick={onClose}>{t('common.close')}</button>
          {!result && (
            <button className="btn btn-primary" onClick={submit} disabled={loading || !file || !zonaId}>
              {loading ? t('colmenas.importImporting') : t('colmenas.importConfirmBtn')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

const S = {
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 },
  title: { fontSize: 26, fontFamily: "'DM Sans', sans-serif" },
  sub: { color: '#8B6355', fontSize: 14, marginTop: 4 },
  filters: { display: 'flex', gap: 12, alignItems: 'center', marginBottom: 24, flexWrap: 'wrap' },
  tabs: { display: 'flex', background: '#F0EDE9', borderRadius: 8, padding: 3, gap: 2 },
  tab: { padding: '6px 14px', borderRadius: 6, border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 13, color: '#8B6355', transition: 'all 0.15s' },
  tabActive: { background: 'white', color: '#2C1A0E', fontWeight: 500, boxShadow: '0 1px 3px rgba(0,0,0,0.1)' },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 14 },
  cardName: { fontWeight: 500, fontSize: 16, color: '#2C1A0E', overflow: 'hidden', textOverflow: 'ellipsis' },
  semaforoBadge: { padding: '3px 10px', borderRadius: 20, fontSize: 12, fontWeight: 500, whiteSpace: 'nowrap' },
  razonChip: { fontSize: 11, fontWeight: 500, background: '#FDECEA', color: '#C0392B', padding: '2px 9px', borderRadius: 20 },
};
